<template>
  <div>
    <div class="payout-bulk-generation">
      <div class="row">
        <div class="col col-12 col-md-2">
          <label class="form__label">Date From:</label>
          <Datepicker
            class="datapicker"
            :bootstrap-styling="true"
            format="yyyy-MM-dd"
            :value="_toIsoStringTime(dateFrom)"
            @input="dateFrom = _toLocalTime($event, defaultDateFormat)"
          />
        </div>
        <div class="col col-12 col-md-2">
          <label class="form__label">Date To:</label>
          <Datepicker
            class="datapicker"
            :bootstrap-styling="true"
            format="yyyy-MM-dd"
            :value="_toIsoStringTime(dateTo)"
            @input="dateTo = _toLocalTime($event, defaultDateFormat)"
          />
        </div>
        <div class="col col-12 col-md-2 align-self-end">
          <b-button
            type="submit"
            class="button generate-invoices-button"
            variant="primary"
            @click.prevent="isShowCreationForm = true">
            Generate invoices
          </b-button>
        </div>
      </div>
    </div>

      <SlideOutContainerViaPortal :loading="isDataLoading">
        <div v-if="isShowCreationForm">
          <BulkCreationFormContainer
            title="Generated invoices"
            :onCloseButtonClick="hideCreationForm"
            :isLoading="isDataLoading"
            :useSlideoutLoading="true"
              :breadcrumbs="[
              {
                text: 'Home',
                to: { name: 'home' },
              },
              {
                text: 'Invoices',
                click: () => closeCreationForm()
              }
            ]"
          >
            <CollapsiblePanel
              title="Generation based on non-covered worklogs"
              bodyInnerClassName="pt-1 pb-0"
            >
              <!-- Fetched invoices info -->
              <div class="row mb-2">
                <div class="col flex-grow-0">
                  <SuccessIcon className="creation-info-success-icon" />
                </div>
                <div class="col flex-grow-1">
                  Fetched <b>{{ dataForCreation.length }} items</b> for creation at
                  period:
                  {{ _toLocalTime(dateFrom, defaultDateFormat) }}
                  &ndash;
                  {{ _toLocalTime(dateTo, defaultDateFormat) }}
                </div>
              </div>

              <BulkCreationTableBaseStyle v-if="messages.length">
                <table :class="{
                  'bulk-creation-table': true,
                  'table-hover': true,
                  'mt-3': true,
                  'has-some-previous': hasSomePrevious
                }">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Creating</th>
                      <th>Invoice for actual period</th>
                      <th v-if="hasSomePrevious" class="actions-column">Previous</th>
                      <th>Status</th>
                      <th class="actions-column">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="({ previous, item, projectName, dateFrom, dateTo, errors, loading, status }, index) in messages"
                      :key="index + '_' + (previous ? previous.id : 0) + '_' + (item ? item.id : 0)"
                    >
                    <td>{{ index + 1 }}</td>
                    <td>
                      {{ projectName }}
                    </td>
                    <td>{{ dateFrom }} &ndash; {{ dateTo }}</td>
                    <td v-if="hasSomePrevious" class="actions-column">
                      <InvoicesLinks
                        v-if="previous"
                        :templates="getTemplates"
                        :item="previous"
                        @deletedInvoice="deleteInvoice('previous', index)"
                        @showEditForm="($event) => { showEditInvoiceForm(messages[index], 'previous', $event); scrollToTheTop(); }"
                      />
                    </td>
                    <td class="status-column">
                      <span v-if="loading" class="small-spinner spinner-border" role="status">
                        <span class="sr-only">Creating...</span>
                      </span>
                      <SuccessIcon v-else-if="status === 'success'" />
                      <ErrorIcon
                        v-else-if="['error', 'warning'].includes(status)"
                        :tooltipHTML="errors && errors.join(' | ')"
                      />
                      <template v-if="item">
                        <span
                          v-if="item.extra_payments.length"
                          class="ep-message"
                          v-b-tooltip.html="{ variant: 'base', placement: 'top', placement:'top', boundary:'viewport' }"
                          :title="'This invoice has extra payments copied from last payee invoice'"
                        >
                          EP<InfoIcon />
                        </span>
                        <span
                          v-if="item && item.extra_payments.length"
                          class="ep-message"
                          v-b-tooltip.html="{ variant: 'base', placement: 'top', placement:'top', boundary:'viewport' }"
                          :title="'In the previous invoice, some items have a non-full-month interval - please make sure the interval in the new invoice is correct'"
                        >
                          PR<InfoIcon />
                        </span>
                      </template>
                    </td>
                    <td class="actions-column">
                      <InvoicesLinks
                        v-if="item"
                        :templates="getTemplates"
                        :item="item"
                        @deletedInvoice="deleteInvoice('auto', index)"
                        @showEditForm="($event) => { showEditInvoiceForm(messages[index], 'item', $event); scrollToTheTop(); }"
                      />
                    </td>
                  </tr>
                  </tbody>
                </table>
              </BulkCreationTableBaseStyle>

              <div v-if="isButtonsBlockVisible" class="d-flex justify-content-center mt-4">
                <button
                  class="btn button btn-primary"
                  v-if="isStopCreationButtonVisible"
                  @click="isProcessedCreation = false">Stop creation ({{ invoicesForCreationCount }} left)</button>
                <button
                  v-else-if="isStartCreationButtonVisible"
                  class="btn button btn-primary"
                  @click="processCreation()">Create {{ invoicesForCreationCount }} invoices</button>
              </div>
            </CollapsiblePanel>

            <CollapsiblePanel
              title="Invoices of types “Only extra payments” or “By project” (created in last 2 months or has worklogs in current month)"
              bodyInnerClassName="pt-0"
            >
              <BulkCreationTableBaseStyle v-if="copyInvoices.length">
                <table class="extra-paymented-invoices-table table-hover">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Name</th>
                      <th>Date</th>
                      <th class="actions-column">Actions</th>
                      <th></th>
                      <th>Name</th>
                      <th>Date</th>
                      <th class="actions-column">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="({ original, item, status }, index) in copyInvoices"
                      :key="index + '_' + (original ? original.id : 0) + '_' + (item ? item.id : 0)"
                    >
                    <td>{{index + 1}}</td>
                    <td>
                      <template v-if="original">{{original.project.name}}</template>
                    </td>
                    <td>
                      <template v-if="original">{{ _toLocalTime(original.issue_date, defaultDateFormat) }}</template>
                    </td>
                    <td class="actions-column">
                      <InvoicesLinks
                        v-if="original"
                        :item="original"
                        :templates="getTemplates"
                        @deletedInvoice="deleteInvoice('original', index)"
                        @showEditForm="($event) => { showEditInvoiceForm(copyInvoices[index], 'original', $event); scrollToTheTop(); }"
                      />
                    </td>
                    <td>
                      <CopyInvoiceButton v-if="status === 'new'" :onClick="() => copyInvoice(index)" />
                      <div v-else-if="status === 'creating'" class="small-spinner spinner-border">
                        <span class="sr-only">Creating...</span>
                      </div>
                      <CopiedStatus v-else-if="status === 'created'" />
                    </td>
                    <td>
                      <template v-if="item">{{item.project.name}}</template>
                    </td>
                    <td>
                      <template v-if="item">{{ _toLocalTime(item.issue_date, defaultDateFormat) }}</template>
                    </td>
                    <td class="actions-column">
                      <InvoicesLinks
                        style="float:right;"
                        v-if="item"
                        :item="item"
                        :templates="getTemplates"
                        @deletedInvoice="deleteInvoice('copy', index)"
                        @showEditForm="($event) => { showEditInvoiceForm(copyInvoices[index], 'item', $event); scrollToTheTop(); }"
                      />
                    </td>
                  </tr>
                  </tbody>
                </table>
              </BulkCreationTableBaseStyle>

              <p v-else>No invoices</p>
            </CollapsiblePanel>
          </BulkCreationFormContainer>
        </div>
      </SlideOutContainerViaPortal>

    <FinanceInvoicesForm
      ref="invoiceFormAuto"
      v-model="invoiceFormData.show"
      :data="invoiceFormData.form"
      :title="invoiceFormData.title"
      :headerTitle="invoiceFormData.headerTitle"
      :submitButtonText="invoiceFormData.submitButtonText"
      :bankDetails="getOwnBankDetails"
      :templates="getTemplates"
      :projects="getActualProjects"
      :statuses="getStatuses"
      :types="getInvoicesTypes"
      :isLoading="getIsModalLoading"
      :breadcrumbs="[
        { text: 'Home', to: { name: 'home' } },
        { text: 'Bulk invoices creation', click: () => hideInvoiceForm() }
      ]"
      @submit="handleBulkEditSubmit"
      @invoiceFormClosed="restoreScrollPosition"
    />
  </div>
</template>

<script>
import { cloneDeep } from 'lodash'
import Moment from 'moment'
import Datepicker from 'vuejs-datepicker'
import { mapActions, mapGetters } from 'vuex'
import { permissions } from '@mocks/permissions'
import InvoicesLinks from '../components/InvoicesLinks'
import InvoiceFormModal from '../mixins/InvoiceFormModal'
import { BulkCreationConfirmTabCloseMixin } from '@mixins/invoices/BulkCreationConfirmTabCloseMixin'
import FinanceInvoicesForm from '@components/modals/FinanceInvoicesForm/FinanceInvoicesForm.vue'
import { STOP_BULK_CREATION_CONFIRMATION_MODAL_CONFIG } from '@/constants/modals/bulk-creation-modals'
import { PostalNames } from '@/constants/PortalNames'
import SlideOutContainerViaPortal from '@components/transition/SlideOutContainerViaPortal.vue'
import CollapsiblePanel from '@components/invoices/CollapsiblePanel.vue'
import BulkCreationFormContainer from '@components/invoices/bulk-creation/BulkCreationFormContainer.vue'
import BulkCreationTableBaseStyle from '@components/invoices/bulk-creation/BulkCreationTableBaseStyle.vue'
import CopyInvoiceButton from '@components/invoices/bulk-creation/CopyInvoiceButton.vue'
import { ErrorIcon, InfoIcon, SuccessIcon } from '@components/icons'
import CopiedStatus from '@components/invoices/bulk-creation/CopiedStatus.vue'
import ScrollPositionMixin from '@mixins/ScrollPositionMixin'

export default {
  name: 'InvoicesBulkCreation',
  mixins: [ InvoiceFormModal, BulkCreationConfirmTabCloseMixin, ScrollPositionMixin ],
  components: {
    CopiedStatus,
    ErrorIcon,
    InfoIcon,
    BulkCreationTableBaseStyle,
    SuccessIcon,
    CollapsiblePanel,
    BulkCreationFormContainer,
    SlideOutContainerViaPortal,
    FinanceInvoicesForm,
    Datepicker,
    InvoicesLinks,
    CopyInvoiceButton
  },
  props: {
    templates: {
      type: Array
    }
  },
  data () {
    const defaultDateFormat = 'YYYY-MM-DD'
    return {
      permissions,
      defaultDateFormat,
      isProcessedCreation: false,
      isEditedItem: false,
      dateFrom: Moment().subtract(1, 'months').startOf('month').format(defaultDateFormat),
      dateTo: Moment().subtract(1, 'months').endOf('month').format(defaultDateFormat),
      isDataLoading: false,
      dataForCreation: [],
      copyInvoices: [],
      messages: [],
      PostalNames
    }
  },
  computed: {
    ...mapGetters({
      getOwnBankDetails: 'finances/getOwnBankDetails',
      getStatuses: 'finances/getStatuses',
      getProjects: 'projects/getProjects',
      getTemplates: 'finances/getTemplates',
      getClientsBankDetailsAll: 'finances/getClientsBankDetailsAll',
      getInvoicesTypes: 'finances/getInvoicesTypes',
      getIsModalLoading: 'finances/getIsModalLoading'
    }),
    getActualProjects () {
      return this.getProjects
        .filter(project => project.project_status.name !== 'done')
    },
    invoicesForCreationCount () {
      return this.dataForCreation.filter(item => !item.processed).length
    },
    currentProcessedInvoiceNumber () {
      return this.dataForCreation.filter(item => item.processed).length + 1
    },
    hasSomePrevious () {
      return this.messages.some(message => message.previous)
    },
    isStopCreationButtonVisible () {
      return this.isProcessedCreation && this.invoicesForCreationCount > 0
    },
    isStartCreationButtonVisible () {
      return this.invoicesForCreationCount > 0
    },
    isButtonsBlockVisible () {
      return this.isStopCreationButtonVisible || this.isStartCreationButtonVisible
    }
  },
  watch: {
    'isShowCreationForm': function (val) {
      if (val) {
        this.generateInvoices()
      }
    }
  },
  methods: {
    Moment: function (date) {
      return Moment(date)
    },
    ...mapActions({
      setIsModalLoading: 'finances/setIsModalLoading'
    }),
    async generateInvoices () {
      this.isShowCreationForm = true

      this.dataForCreation = []
      this.copyInvoices = []
      this.messages = []

      this.isDataLoading = true
      const dataForCreationResponse = await this.fetchDataForCreation()
      this.isDataLoading = false

      this.dataForCreation = Object.values(dataForCreationResponse.data.availablePeriods).map(item => {
        item.processed = false
        return item
      })
      this.copyInvoices = Object.values(dataForCreationResponse.data.copyInvoices).map(item => {
        return {
          original: item,
          item: null,
          status: 'new'
        }
      })
    },
    closeCreationForm () {
      this.isShowCreationForm = false
      this.isProcessedCreation = false
    },
    async hideCreationForm () {
      const confirm = await this.$root.$confirm.open(...STOP_BULK_CREATION_CONFIRMATION_MODAL_CONFIG)
      if (confirm) {
        this.isShowCreationForm = false
        this.isProcessedCreation = false
        if (this.messages.filter(item => item.type === 'creation' && item.status === 'success').length > 0) {
          this.$emit('updateInvoices')
        }
      }
    },
    saveMessageSuccess (message, item) {
      const itemAttrName = message.itemAttrName ?? 'item'
      if (itemAttrName === 'item') {
        message.status = 'success'
      }
      message[itemAttrName] = item ? Object.assign(message[itemAttrName], item) : message[itemAttrName]
      message.errors = []
      message.loading = false
    },
    saveMessageError (message, errors) {
      message.status = 'error'
      message.errors = message.errors ? message.errors.push(errors) : errors
      message.loading = false
    },
    async processCreation () {
      this.isProcessedCreation = true

      for (const index in this.dataForCreation) {
        const item = this.dataForCreation[index]
        if (!this.isProcessedCreation) {
          break
        }
        if (item.processed) {
          continue
        }

        const dateFrom = this.dateFrom
        const dateTo = this.dateTo

        this.messages.push({
          type: 'creation',
          status: 'in-progress',
          projectName: item.project.name,
          dateFrom: this._toLocalTime(dateFrom, this.defaultDateFormat),
          dateTo: this._toLocalTime(dateTo, this.defaultDateFormat),
          loading: true
        })

        item.processed = true
        this.dataForCreation[index] = item

        let processedInvoiceMessage = this.messages.at(-1)

        let invoiceData = {}
        this.$refs['invoiceFormAuto'].resetForm()

        this.$refs['invoiceFormAuto'].form.fields.project.value = cloneDeep(item.project)
        this.$refs['invoiceFormAuto'].form.fields.invoice_status.value = this.getInvoiceStatus('new')
        await this.$refs['invoiceFormAuto'].replaceCommentsWithProjectName()
        this.$refs['invoiceFormAuto'].form.fields.auto_fill.value = cloneDeep({ date_from: dateFrom, date_to: dateTo })

        const formFields = cloneDeep(this.$refs['invoiceFormAuto'].form.fields)
        Object.keys(formFields).forEach(function (key) {
          invoiceData[key] = formFields[key].value
        })

        const projectId = item.project.id
        let errors = []
        const clientBankDetailsData = await this.$refs['invoiceFormAuto'].fetchClientBanks(projectId)

        if (!clientBankDetailsData || clientBankDetailsData.length === 0) {
          errors.push('Project has no client bank details')
          processedInvoiceMessage.item = cloneDeep(invoiceData)
          this.saveMessageError(processedInvoiceMessage, errors)
          continue
        }

        const { data: lastProjectInvoiceData } = await this.$refs['invoiceFormAuto'].fetchPreviousProjectInvoice(projectId)

        if (Object.keys(lastProjectInvoiceData).length) {
          invoiceData['extra_payments'] = lastProjectInvoiceData.extra_payments
          invoiceData['client_bank_details'] = lastProjectInvoiceData.client_bank_details
          invoiceData['own_bank_details'] = lastProjectInvoiceData.own_bank_details
          if (lastProjectInvoiceData.invoice_template_type.name !== 'custom') {
            invoiceData['invoice_template_type'] = lastProjectInvoiceData.invoice_template_type
          }
          this.$refs['invoiceFormAuto'].previousInvoiceData.items = lastProjectInvoiceData.items
          this.$refs['invoiceFormAuto'].previousInvoiceData.periods = lastProjectInvoiceData.periods

          const checkNotMonthPeriods =
            item => this.Moment(item.date_from).format(this.defaultDateFormat) !== this.Moment(item.date_from).startOf('month').format(this.defaultDateFormat) ||
                    this.Moment(item.date_to).format(this.defaultDateFormat) !== this.Moment(item.date_to).endOf('month').format(this.defaultDateFormat)

          const lastProjectInvoiceType = lastProjectInvoiceData.invoice_type

          if (this.$refs['invoiceFormAuto'].isEmployeeTm(lastProjectInvoiceType.id)) {
            if (lastProjectInvoiceData.items.some(checkNotMonthPeriods)) {
              invoiceData.hasNotMonthPeriods = true
              processedInvoiceMessage.previous = lastProjectInvoiceData
            }
            invoiceData['invoice_type'] = lastProjectInvoiceType
            invoiceData['items'] = lastProjectInvoiceData.items
          }

          if (this.$refs['invoiceFormAuto'].isEmployeePeriod(lastProjectInvoiceType.id)) {
            if (lastProjectInvoiceData.periods.some(checkNotMonthPeriods)) {
              invoiceData.hasNotMonthPeriods = true
              processedInvoiceMessage.previous = lastProjectInvoiceData
            }
            invoiceData['invoice_type'] = lastProjectInvoiceType
            invoiceData['periods'] = lastProjectInvoiceData.periods
          }

          invoiceData['sales_commission_percent'] = lastProjectInvoiceData.sales_commission_percent
          invoiceData['commission'] = lastProjectInvoiceData.commission
        } else {
          invoiceData['client_bank_details'] = clientBankDetailsData[0]
          const currencyCode = invoiceData['client_bank_details'].currency.code
          invoiceData['own_bank_details'] = this.$refs['invoiceFormAuto'].form.fields.own_bank_details.options.find(item => item.currency.code === currencyCode)
        }

        if (!invoiceData.client_bank_details) {
          errors.push('Client bank details is required')
        }
        if (!invoiceData.own_bank_details) {
          errors.push('Own bank details is required')
        }

        if (errors.length > 0) {
          processedInvoiceMessage.item = cloneDeep(invoiceData)
          this.saveMessageError(processedInvoiceMessage, errors)
          continue
        }

        this.$refs['invoiceFormAuto'].form.fields.invoice_type.value = invoiceData.invoice_type

        await this.$refs['invoiceFormAuto'].setParamsForEditInvoiceForm(invoiceData)

        const autoFillItems = await this.$refs['invoiceFormAuto'].autoFillRequest({
          project_id: projectId, invoice_type_id: invoiceData['invoice_type']['id'], date_from: dateFrom, date_to: dateTo
        })

        if (!autoFillItems) {
          errors.push('No items for project available')
        } else {
          if (this.$refs['invoiceFormAuto'].isEmployeeTm()) {
            invoiceData.items = cloneDeep(this.$refs['invoiceFormAuto'].form.fields.items.value)
            if (invoiceData.items.some(item => item.hour_cost === 0)) {
              errors.push('Need set hours cost for items')
            }
          } else if (this.$refs['invoiceFormAuto'].isEmployeePeriod()) {
            invoiceData.periods = cloneDeep(this.$refs['invoiceFormAuto'].form.fields.periods.value)
            if (invoiceData.periods.some(item => item.period_cost === 0)) {
              errors.push('Need set period cost for items')
            }
          }
        }

        processedInvoiceMessage.item = cloneDeep(invoiceData)

        if (errors.length > 0) {
          this.saveMessageError(processedInvoiceMessage, errors)
          continue
        }

        await this.$refs['invoiceFormAuto'].setParamsForEditInvoiceForm(invoiceData)
        const invoiceDataRequest = this.prepareDataForEditAndCreateRequest(this.$refs['invoiceFormAuto'].form.fields)
        await this.saveInvoiceBulk(invoiceDataRequest, processedInvoiceMessage)
      }
    },
    async saveInvoiceBulk (invoiceDataForRequest, invoiceMessage) {
      const that = this
      return this.saveInvoice(invoiceDataForRequest, invoiceMessage, async function () {
        if (invoiceDataForRequest.id) {
          return that.$api.finances.updateInvoices(invoiceDataForRequest)
        } else {
          return that.$api.finances.createInvoices(invoiceDataForRequest)
        }
      })
    },
    async showEditInvoiceForm (message, attrName, invoice) {
      this.isEditedItem = true
      message.itemAttrName = attrName
      this.invoiceFormData.message = message
      this.invoiceFormData.attrName = attrName

      // Fetch fresh invoice data from API to ensure we have the latest version
      if (invoice && invoice.id) {
        try {
          this.setIsModalLoading(true)
          const freshInvoice = await this.$api.finances.getInvoice(invoice.id)
          // Update the message with fresh data
          message[attrName] = freshInvoice
          this.showEditInvoice(freshInvoice, freshInvoice)
        } catch (error) {
          this.errorNotifications(error)
          this.setIsModalLoading(false)
        }
      } else {
        this.showEditInvoice(invoice, message[attrName])
      }
    },
    async handleBulkEditSubmit (fields) {
      try {
        await this.saveInvoiceFromMixin(fields)
        // Update local state with saved data
        if (this.isEditedItem && this.invoiceFormData.message) {
          const attrName = this.invoiceFormData.attrName
          this.invoiceFormData.message[attrName] = this.invoiceFormData.item
        }
        this.isEditedItem = false
        this.hideInvoiceForm()
        this.restoreScrollPosition()
      } catch (e) {
        // Error handled by mixin
      }
    },
    async saveInvoiceFromMixin (fields) {
      if (this.invoiceFormData.form && this.invoiceFormData.form.id) {
        const { data } = await this.updateInvoice(fields)
        this.invoiceFormData.item = data
      } else {
        const { data } = await this.createInvoice(fields)
        this.invoiceFormData.item = data
      }
    },
    async saveInvoice (invoiceDataForRequest, invoiceMessage, saveInvoiceFunc) {
      try {
        let invoiceResponse
        invoiceResponse = await saveInvoiceFunc()
        const invoiceData = invoiceResponse.data
        if (invoiceResponse.status === 200) {
          this.saveMessageSuccess(invoiceMessage, invoiceData)
        }
      } catch (e) {
        const errors = []
        if (e.response.data.errors) {
          errors.push(Object.values(e.response.data.errors).map(itemErrors => itemErrors[0]))
        } else {
          errors.push(e.message)
        }

        this.saveMessageError(invoiceMessage, errors)
      }
    },
    deleteInvoice (type, index) {
      if (type === 'auto' || type === 'previous') {
        this.messages.splice(index, 1)
      } else if (type === 'copy') {
        if (!this.copyInvoices[index]['original']) {
          this.copyInvoices.splice(index, 1)
        } else {
          this.copyInvoices[index]['item'] = null
          this.copyInvoices[index].status = 'new'
        }
      } else if (type === 'original') {
        if (!this.copyInvoices[index]['item']) {
          this.copyInvoices.splice(index, 1)
        } else {
          this.copyInvoices[index]['original'] = null
        }
      }
    },
    fetchDataForCreation () {
      return this.axios
        .post('/api/finances/invoices/data-for-bulk-creation',
          {
            date_from: this.dateFrom,
            date_to: this.dateTo
          },
          { headers: { Authorization: 'Bearer' + localStorage.getItem('access_token') } })
        .catch((error) => {
          this.errorNotifications(error)
        })
    },
    async copyInvoice (index) {
      const original = this.copyInvoices[index].original
      const item = cloneDeep(original)
      this.copyInvoices[index].status = 'creating'

      item.invoice_status = this.getInvoiceStatus('new')
      await this.$refs['invoiceFormAuto'].setParamsForEditInvoiceForm(item)
      const invoiceDataRequest = this.prepareDataForEditAndCreateRequest(this.$refs['invoiceFormAuto'].form.fields)

      invoiceDataRequest.items = []
      invoiceDataRequest.periods = []
      invoiceDataRequest.issue_date = this.Moment().format(this.defaultDateFormat)
      invoiceDataRequest.due_date = this.Moment().add(3, 'days').format(this.defaultDateFormat)
      invoiceDataRequest.paid_date = null

      const invoiceResponse = await this._toggleDataPageLoading(async () => {
        return this.$api.finances.createInvoices(invoiceDataRequest)
      })
      if (invoiceResponse.data) {
        this.copyInvoices[index].item = invoiceResponse.data
        this.copyInvoices[index].status = 'created'
      } else {
        this.copyInvoices[index].item = item
        this.copyInvoices[index].status = 'error'
      }
    },
    getInvoiceStatus (name) {
      return this.$refs['invoiceFormAuto'].statuses.find(el => el.name === name)
    }
  }
}
</script>

<style lang="scss" scoped>
.generate-invoices-button {
  width: 100%;
  justify-content: center;
}

.creation-info-success-icon {
  margin-top: -3px;
}

.bulk-creation-table {
  td, th {
    width: 33%;

    &:first-child {
      width: 0 !important;
    }

    &:last-child {
      min-width: 136px;
    }
  }

  &.has-some-previous td, th {
    width: 25%;
  }

  td {
    &.actions-column {
      padding-left: 3px;
    }
  }
}

.extra-paymented-invoices-table {
  td, th {
    width: calc(100% / 6);

    &:first-child {
      width: 0;
    }

    &:last-child {
      min-width: 136px;
    }
  }

  td {
    &.actions-column {
      padding-left: 3px;
    }
  }
}
</style>
